import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Chip, CircularProgress, Grid, Typography } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(3)
    },
    title: {
        marginBottom: theme.spacing(1)
    },
    tableHeader: {
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.colors.ghostwhite
    },
    tableRow: {
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.palette.common.white
    },
    empty: {
        color: theme.colors.steel,
        padding: theme.spacing(2),
        background: theme.palette.common.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 8
    },
    linkButton: {
        textTransform: 'none'
    }
}));

function formatAmount(amount, currency) {
    const value = Number(amount || 0) / 100;
    try {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: (currency || 'cad').toUpperCase()
        }).format(value);
    } catch (error) {
        return `${(currency || 'cad').toUpperCase()} ${value.toFixed(2)}`;
    }
}

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
}

function ChargesTable({ charges = [], isLoading, error }) {
    const classes = useStyles();

    return (
        <Grid className={classes.root} container direction="column">
            <Grid item>
                <Typography className={classes.title} variant="body1">
                    Charges
                </Typography>
            </Grid>
            {isLoading ? (
                <Grid item>
                    <CircularProgress size={24} />
                </Grid>
            ) : error ? (
                <Typography className={classes.empty} color="secondary" variant="body2">
                    {error?.message || 'Load charge history failed'}
                </Typography>
            ) : charges.length ? (
                <Grid item>
                    <Grid container direction="column">
                        <Grid item className={classes.tableHeader}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Invoice</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Status</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Paid</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Period</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Created</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Links</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        {charges.map((charge) => (
                            <Grid item className={classes.tableRow} key={charge?.id}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={2}>
                                        <Typography variant="body2">{charge?.number || charge?.id}</Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Chip size="small" label={charge?.status || '-'} />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="body2">
                                            {formatAmount(charge?.amountPaid || charge?.total, charge?.currency)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="body2">
                                            {formatDate(charge?.periodStart)} - {formatDate(charge?.periodEnd)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="body2">{formatDate(charge?.createdAt)}</Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Grid container spacing={1}>
                                            {charge?.hostedInvoiceUrl && (
                                                <Grid item>
                                                    <Button
                                                        className={classes.linkButton}
                                                        href={charge.hostedInvoiceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        size="small"
                                                        color="primary"
                                                    >
                                                        Invoice
                                                    </Button>
                                                </Grid>
                                            )}
                                            {charge?.receiptUrl && (
                                                <Grid item>
                                                    <Button
                                                        className={classes.linkButton}
                                                        href={charge.receiptUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        size="small"
                                                        color="primary"
                                                    >
                                                        Receipt
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ) : (
                <Typography className={classes.empty} variant="body2">
                    No charges yet.
                </Typography>
            )}
        </Grid>
    );
}

export default React.memo(ChargesTable);
